/**
 * ExcludePresets — one-click rule starters (conditions, and the two partner-cap rules).
 * For Media Devoted | Created By Ciro Autiero
 *
 * Each chip prints the condition it will create, so picking one is an informed
 * choice rather than a surprise: the rule it adds is exactly what the chip says.
 * The partner-cap presets are queue rules — not offered without a queue.
 */

import { FC } from 'react';
import { Plus } from 'lucide-react';
import { useDrawerProfile } from '@/features/flow-optimizer/tracker';

import { EXCLUDE_PRESETS, type ExcludePreset } from '../exclude-presets';
import { QUEUE_LIFECYCLE_ACTIONS } from '../rule-action-compat';

export interface ExcludePresetsProps {
  onApply: (preset: ExcludePreset) => void;
  layer: string;
}

const needsQueue = (preset: ExcludePreset): boolean =>
  (preset.actions ?? []).some((a) => QUEUE_LIFECYCLE_ACTIONS.includes(a));

const ExcludePresets: FC<ExcludePresetsProps> = ({ onApply, layer }) => {
  const { queue } = useDrawerProfile();
  const presets = queue ? EXCLUDE_PRESETS : EXCLUDE_PRESETS.filter((p) => !needsQueue(p));
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {presets.map((preset) => (
        <button
          key={preset.id}
          type="button"
          onClick={() => onApply(preset)}
          aria-label={`Apply preset: ${preset.label} on ${layer}`}
          className="inline-flex h-7 items-center gap-1.5 rounded-full border border-border bg-background px-2.5 text-[11px] font-medium text-foreground transition-colors hover:border-success/50 hover:bg-success/5 hover:text-success"
        >
          <Plus className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          {preset.label}
          <span className="font-mono text-[10px] text-muted-foreground">
            {preset.hint}
          </span>
        </button>
      ))}
    </span>
  );
};

export default ExcludePresets;
